import { Check, Copy, Inbox, Loader2, OctagonAlert, RefreshCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { isApiError } from '../api';
import { errorText } from '../lib/errorText';

export function Loading({ label, rows = 0 }: { label?: string; rows?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite" className="space-y-3 py-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        {label ?? t('state.loading')}
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({ title, children, icon: Icon = Inbox, className }: { title: string; children?: ReactNode; icon?: typeof Inbox; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center', className)}>
      <div className="flex size-10 items-center justify-center rounded-full bg-muted">
        <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
      </div>
      <strong className="text-sm font-semibold">{title}</strong>
      {children ? <div className="max-w-prose text-sm text-muted-foreground">{children}</div> : null}
    </div>
  );
}

/** Renders an ApiError from the stable envelope, surfacing `request_id` for support. */
export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const { t } = useTranslation();
  const apiError = isApiError(error) ? error : null;
  const { text: message, detail } = errorText(error);
  return (
    <div role="alert" className="space-y-3 rounded-lg border border-critical/30 bg-critical-soft/60 p-4 text-sm">
      <div className="flex items-center gap-2 font-semibold text-critical">
        <OctagonAlert className="size-4" aria-hidden="true" />
        {title ?? t('state.error')}
      </div>
      <p className="text-foreground">{message}</p>
      <TechnicalDetail detail={detail} />
      {apiError ? (
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
          <div className="flex items-center gap-1.5">
            <dt className="text-muted-foreground">{t('state.code')}</dt>
            <dd>
              <code>{apiError.code}</code>
              {apiError.status ? <span className="text-muted-foreground"> · HTTP {apiError.status}</span> : null}
            </dd>
          </div>
          <div className="flex items-center gap-1.5">
            <dt className="text-muted-foreground">{t('state.requestId')}</dt>
            <dd>
              <CopyText value={apiError.requestId} />
            </dd>
          </div>
        </dl>
      ) : null}
      {onRetry ? (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw /> {t('state.retry')}
        </Button>
      ) : null}
    </div>
  );
}

/** The server's own sentence behind a translated error, folded away (for support). */
export function TechnicalDetail({ detail }: { detail: string | null }) {
  const { t } = useTranslation();
  if (!detail) return null;
  return (
    <details className="text-xs text-muted-foreground">
      <summary className="cursor-pointer select-none">{t('apiErrors.detail')}</summary>
      <p className="mt-1 break-words font-mono">{detail}</p>
    </details>
  );
}

/** Monospace identifier with a copy-to-clipboard button. */
export function CopyText({ value, display }: { value: string; display?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };
  return (
    <span className="inline-flex max-w-full items-center gap-1 align-middle">
      <code className="truncate rounded bg-muted px-1.5 py-0.5 text-xs" title={value}>
        {display ?? value}
      </code>
      <button
        type="button"
        className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        onClick={copy}
        aria-label={copied ? t('state.copied') : t('state.copy', { value })}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </button>
    </span>
  );
}
